import React from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const focusAreas = [
  {
    image: "/foucs/Governance & Crisis Management.jpg",
    title: "Governance & Crisis Management",
    description:
      "Strengthening institutions, enhancing governance, and supporting states and organizations in times of crisis or geopolitical instability.",
    color: "from-blue-500 to-blue-600",
  },
  {
    image: "/foucs/Energy Security & Green Transition.webp",
    title: "Energy Security & Green Transition",
    description:
      "Advancing renewable energy, reducing fossil fuel dependence, and ensuring sustainable resource management for resilient societies.",
    color: "from-green-500 to-green-600",
  },
  {
    image: "/foucs/Innovation & Future Readiness.webp",
    title: "Innovation & Future Readiness",
    description:
      "Driving sustainable agriculture, food and water security, digital transformation, and capacity-building for the next generation.",
    color: "from-purple-500 to-purple-600",
  },
  {
    image: "/foucs/Climate Action & Ecosystems.webp",
    title: "Climate Action & Ecosystems",
    description:
      "Tackling emissions, protecting biodiversity, and safeguarding land and sea in the Mediterranean and Aegean.",
    color: "from-teal-500 to-teal-600",
  },
  {
    image: "/foucs/Social Resilience.webp",
    title: "Social Resilience",
    description:
      "Reducing poverty and inequalities while promoting health, education, fair employment, and inclusive growth.",
    color: "from-indigo-500 to-indigo-600",
  },
];

export default function Home() {
  return (
    <div>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Background video */}
        <div className="absolute inset-0">
          <video
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            src="/bg.mp4"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #93C754, #7FB740, #5C8A24)",
                }}
              >
                Theophrastus
              </span>
              <br />
              <span className="text-white">Research Lab</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Combining scientific research and strategic consulting to address
              the pressing challenges of our time.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8">
              <Link
                to="/about"
                className="group bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center space-x-2"
                style={{ boxShadow: "0 0 0 2px #7FB740 inset" }}
              >
                <span>Discover Our Mission</span>
                <ArrowRight
                  size={20}
                  className="group-hover:translate-x-1 transition-transform"
                  style={{ color: "#7FB740" }}
                />
              </Link>
              <Link
                to="/contact"
                className="group border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105 backdrop-blur-md"
                style={{
                  borderColor: "#7FB740",
                  color: "#7FB740",
                  backgroundColor: "rgba(127,183,64,0.1)",
                }}
              >
                Start a Partnership
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown size={32} className="text-white/70" />
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Focus Areas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We address today's most pressing challenges through five key areas
              of expertise, delivering science-driven solutions for a
              sustainable future.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {focusAreas.map((area, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 overflow-hidden last:md:col-span-2 last:md:justify-self-center last:md:max-w-3xl"
              >
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                  style={{
                    backgroundImage: "linear-gradient(135deg,#93C754,#7FB740)",
                  }}
                ></div>
                <div className="relative z-10">
                  <div className="-mx-8 -mt-8 mb-6 overflow-hidden rounded-t-3xl">
                    <div className="w-full aspect-[16/9]">
                      <img
                        src={area.image}
                        alt={area.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {area.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {area.description}
                  </p>
                </div>
                <div
                  className={`absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  style={{
                    backgroundImage: "linear-gradient(90deg,#93C754,#7FB740)",
                  }}
                ></div>
              </div>
            ))}
          </div>
          <div
            className="relative text-white p-12 rounded-3xl shadow-2xl overflow-hidden"
            style={{
              background: "linear-gradient(90deg,#93C754,#7FB740,#5C8A24)",
            }}
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10 text-center">
              <div className="flex items-center justify-center mb-6">
                <ArrowRight className="text-gray-300 mr-3" size={28} />
                <h3 className="text-2xl font-bold">Our Impact</h3>
              </div>
              <p className="text-lg font-medium max-w-4xl mx-auto leading-relaxed">
                Through research, consulting, and capacity-building, TRL
                delivers science-driven strategies to secure a greener, safer,
                and more resilient future — starting in the Aegean, extending to
                Greece, and contributing to Europe.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
